import React, { Fragment, useEffect } from 'react';
import { FadeUp } from '../../common/Animations';
import { SoldOutIconV2, XMarkCircleIconV2 } from '../icons';
import ButtonV2, { ButtonVariants } from '../common/ButtonV2';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import {
  selfListingInitialState,
  updateContinueListingData,
  updateSelfListingFlow,
} from '../../../lib/self-listing/selfListing.slice';
import { CheckIconWithGreenFill } from '../../icons/CheckIconWithGreenFill';
import { useTranslationContext } from '../../../provider/TranslationProvider';
import Image from 'next/image';

interface ContinueListingMessagePopupProps {
  status:
    | 'invalidId'
    | 'dataError'
    | 'apiError'
    | 'invalidUser'
    | 'alreadyListed'
    | 'sold'
    | 'deleted';
}

const ContinueListingMessagePopup: React.FC<
  ContinueListingMessagePopupProps
> = ({ status }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { LabelConstants } = useTranslationContext();

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const getMessage = (): string => {
    switch (status) {
      case 'alreadyListed':
        return LabelConstants?.CONTINUE_SL_ALREADY_LISTING;
      case 'invalidUser':
        return LabelConstants?.CONTINUE_SL_INVALID_USER_ERROR;
      case 'sold':
        return LabelConstants?.CONTINUE_SL_LISTING_SOLD;
      case 'deleted':
        return LabelConstants?.CONTINUE_SL_LISTING_DELETED;
      default:
        return LabelConstants?.CONTINUE_SL_LINK_BROKEN_ERROR;
    }
  };

  const getMessageIcon = () => {
    switch (status) {
      case 'alreadyListed':
        return <CheckIconWithGreenFill />;
      case 'sold':
        return <SoldOutIconV2 className="!w-[100px] !h-[100px]" />;
      default:
        return (
          <XMarkCircleIconV2
            circleColor="#FF99996C"
            xColor="#EE4D37"
            className="!w-[48px] !h-[48px]"
          />
        );
    }
  };

  return (
    <div className="fixed top-0 left-0 h-full w-full z-[101] flex items-center justify-center bg-black/80">
      <Fragment>
        <FadeUp
          duration={0.25}
          className={`w-full h-full p-[16px] flex flex-col gap-[24px] items-center justify-between rounded-none lg:rounded-[20px] lg:w-[600px] lg:h-[350px] bg-white`}
        >
          <span></span>
          <div className="flex flex-col items-center justify-center gap-[24px]">
            {getMessageIcon()}
            <p className="text-18 max-w-[300px] text-center">{getMessage()}</p>
          </div>
          <div className="mt-[32px] flex flex-col sm:flex-row w-full items-center justify-center gap-[16px]">
            <ButtonV2
              variant={ButtonVariants.PRIMARY}
              onClick={() => {
                dispatch(
                  updateContinueListingData({ showInvalidUserError: false })
                );
                dispatch(updateSelfListingFlow(selfListingInitialState.data));
                router.push(
                  router.locale === router.defaultLocale
                    ? '/sell-my-car'
                    : `/${router.locale}/sell-my-car`
                );
              }}
              className="hover:transform hover:translate-y-[-2px] !w-full sm:!w-auto transition-transform duration-200"
            >
              {LabelConstants?.START_A_NEW_LISTING}
            </ButtonV2>
            <ButtonV2
              onClick={() =>
                router.push(
                  router.locale === router.defaultLocale
                    ? '/'
                    : `/${router.locale}`
                )
              }
              className="px-[20px] !py-[12px] hover:transform !w-full sm:!w-auto hover:translate-y-[-2px] transition-transform duration-200"
              variant={ButtonVariants.OUTLINED}
            >
              {LabelConstants?.RETURN_TO_HOMEPAGE}
            </ButtonV2>
          </div>
        </FadeUp>
      </Fragment>
    </div>
  );
};

export default ContinueListingMessagePopup;
